import { useEffect, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

import { TimePicker } from 'antd';
import { addDays, format } from 'date-fns';
import dayjs from 'dayjs';
import { useFormik } from 'formik';

import PopUp from '@/components/Popup/PopUp/PopUp';
import Button from '@/components/shared/Button/Button';
import { Text } from '@/components/shared/Text/Text';
import { daysOfWeek } from '@/lib/constants';
import { useCoachCabinetStore } from '@/lib/store/coachCabinetStore';
import { CoachSchedule } from '@addmile/backend-client';

import { calculateCustomHours, validateTimeslot } from './validations';

interface CustomTimeslotsModalProps {
  isPopupOpen: boolean;
  closePopup: () => void;
  schedule: CoachSchedule;
  onAddCustomHours: (customHours: Array<{ fromDateTime: string; toDateTime: string }>) => void;
}

const CustomTimeslotsModal = ({
  isPopupOpen,
  closePopup,
  schedule,
  onAddCustomHours,
}: CustomTimeslotsModalProps) => {
  const coach = useCoachCabinetStore((state) => state.coach);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [timeslotsByDate, setTimeslotsByDate] = useState<{
    [key: string]: Array<{ fromTime: string; toTime: string }>;
  }>({});

  const today = new Date();

  const formik = useFormik({
    initialValues: { fromTime: '', toTime: '' },
    onSubmit: (values) => {
      if (!selectedDate) return;

      const dateKey = format(selectedDate, 'yyyy-MM-dd');
      const currentTimeslots = timeslotsByDate[dateKey] || [];

      const errors = validateTimeslot(currentTimeslots, values, -1);
      if (Object.keys(errors).length === 0) {
        const updatedTimeslots = [...currentTimeslots, values];
        setTimeslotsByDate({
          ...timeslotsByDate,
          [dateKey]: updatedTimeslots,
        });
        formik.resetForm();
      } else {
        formik.setErrors(errors);
      }
    },
  });

  useEffect(() => {
    if (formik.values.fromTime && formik.values.toTime) {
      formik.handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.fromTime, formik.values.toTime]);

  const handleAddCustomHours = () => {
    if (!selectedDate) return;

    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    const timeslots = timeslotsByDate[dateKey] || [];

    const customHours = timeslots.map((slot) => {
      return calculateCustomHours(selectedDate, slot.fromTime, slot.toTime, coach.timezone);
    });

    onAddCustomHours([...(schedule.customHours || []), ...customHours]);
    closePopup();
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleTimeChange = (field: 'fromTime' | 'toTime', timeString: string) => {
    formik.setFieldValue(field, timeString);
  };

  const isButtonDisabled = useMemo(() => {
    if (!schedule.workingHours) {
      return true;
    }

    return daysOfWeek.some((day) => {
      return schedule.workingHours[day]?.some((slot) => !slot.fromTime || !slot.toTime);
    });
  }, [schedule.workingHours]);

  if (!schedule) return null;

  const dateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const currentTimeslots = timeslotsByDate[dateKey] || [];

  return (
    <PopUp isOpen={isPopupOpen} handleClose={closePopup} centered maxWidth={680}>
      <div className="flex flex-col gap-4">
        <Text.H4 className="font-bold">Add Custom Hours</Text.H4>
        <div className="flex justify-between gap-4">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={{ before: addDays(today, 1) }}
          />

          {selectedDate && (
            <div className="flex flex-col gap-2 w-[272px]">
              <Text.H5 className="font-medium">{selectedDate.toDateString()}</Text.H5>
              <div className="flex gap-2">
                <TimePicker
                  name="fromTime"
                  format="HH:mm"
                  placeholder="Start Time"
                  value={formik.values.fromTime ? dayjs(formik.values.fromTime, 'HH:mm') : null}
                  onChange={(time, timeString) =>
                    handleTimeChange('fromTime', timeString as string)
                  }
                  maxDate={formik.values.toTime ? dayjs(formik.values.toTime, 'HH:mm') : undefined}
                />

                <TimePicker
                  name="toTime"
                  format="HH:mm"
                  placeholder="End Time"
                  value={formik.values.toTime ? dayjs(formik.values.toTime, 'HH:mm') : null}
                  onChange={(time, timeString) => handleTimeChange('toTime', timeString as string)}
                  minDate={
                    formik.values.fromTime ? dayjs(formik.values.fromTime, 'HH:mm') : undefined
                  }
                />
              </div>
              {formik.errors.fromTime && (
                <Text.Regular className="text-warning-60">{formik.errors.fromTime}</Text.Regular>
              )}
              {formik.errors.toTime && (
                <Text.Regular className="text-warning-60">{formik.errors.toTime}</Text.Regular>
              )}
              {currentTimeslots.map((slot, index) => (
                <div key={slot.toTime} className="flex gap-2 items-center">
                  <span>
                    {slot.fromTime} - {slot.toTime}
                  </span>
                  <Button
                    isIcon
                    variant="transparent"
                    className="w-8 h-8 border-none"
                    onClick={() => {
                      const updatedTimeslots = currentTimeslots.filter((_, i) => i !== index);
                      setTimeslotsByDate({
                        ...timeslotsByDate,
                        [dateKey]: updatedTimeslots,
                      });
                    }}
                    prefixIcon={<img src={'/icons/close.svg'} />}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          disabled={isButtonDisabled}
          variant="primary"
          onClick={handleAddCustomHours}
          label="Save Hours"
        />
      </div>
    </PopUp>
  );
};

export default CustomTimeslotsModal;
